import { useCallback } from 'react';
import { useAuthStore } from './useAuthStore';
import type { LoginCredentials, RegisterData, Role } from '../../types/auth';

/**
 * Framework-agnostic auth hook — no router dependency (works in both the Vite
 * admin app and the Next.js customer app). Navigation on logout is the caller's
 * responsibility (subscribe to `onAuthFailure` from the api layer, or redirect
 * after `logout()`).
 */
export const useAuth = () => {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const storeLogin = useAuthStore((s) => s.login);
  const storeRegister = useAuthStore((s) => s.register);
  const storeLogout = useAuthStore((s) => s.logout);
  const hasRole = useAuthStore((s) => s.hasRole);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const isPlatformAdmin = useAuthStore((s) => s.isPlatformAdmin);
  const getDisplayName = useAuthStore((s) => s.getDisplayName);
  const getUserInitials = useAuthStore((s) => s.getUserInitials);

  const login = useCallback((credentials: LoginCredentials) => storeLogin(credentials), [storeLogin]);
  const register = useCallback((data: RegisterData) => storeRegister(data), [storeRegister]);
  const logout = useCallback(() => storeLogout(), [storeLogout]);

  return {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    register,
    logout,
    hasRole,
    hasAnyRole: (roles: Role[]) => hasRole(roles),
    isAdmin: isAdmin(),
    isPlatformAdmin: isPlatformAdmin(),
    isManager: hasRole('manager'),
    isWorker: hasRole('worker'),
    isCustomer: hasRole('customer'),
    getDisplayName,
    getUserInitials,
    userId: user?.id,
    userEmail: user?.email,
    userName: user?.name,
    userRoles: user?.roles ?? [],
  };
};
